import { useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "antd";
import { UserOutlined, CheckCircleFilled, SyncOutlined, EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { UiButton, UiSearch, UiTable, UiTextBox, UiCounterBatch } from "../../../components";
import AddUserDrawer from "./addUserDrawer";
import { USER_COLUMNS } from "./config";
import * as api from "../../../actions";
import { ADMINUSERMANAGEMENT, USERMANAGEMENT } from "../../../apiServices/endpoints";
import { loaderReducer } from "../../../reducer/loader.reducer";
import { showErrorNotification, showSuccessNotification } from "../../../components/uiNotification";
import { searchInitiateDelayTime } from "../../../utils/appUtils";

const initialFilterState = { page: 0, size: 10, searchFilter: "", searchTermValue: "" };
const initialPasswordFormState = { password: "", confirmPassword: "", passwordError: "", confirmPasswordError: "" };
const getInitialFormData = (defaultRoleValue, defaultSalutationValue) => ({ firstName: "", middleName: "", lastName: "", salutation: defaultSalutationValue, mobileNumber: "", emailId: "", role: defaultRoleValue, password: "", confirmPassword: "" });

const UserManagement = () => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const debounceTimeOut = useRef(null);
    const configResults = useSelector((state) => state?.configAction?.configData);
    const adminRoleConfig = configResults?.find((detail) => detail?.configId === "AdminRoles");
    const salutationConfig = configResults?.find((detail) => detail?.configId === "salutationList");
    const configTypeOptions = adminRoleConfig?.configuration?.map((config) => ({ label: config, value: config, key: config })) || [];
    const salutationOptions = salutationConfig?.configuration?.map((config) => ({ label: config, value: config, key: config })) || [];
    const defaultRoleValue = adminRoleConfig?.defaultValue || null;
    const defaultSalutationValue = salutationConfig?.defaultValue || null;
    const [modalState, setModalState] = useState({
        isResetModalOpen: false,
        isPasswordFormOpen: false,
        isStatusModalOpen: false,
        isAddUserDrawerOpen: false,
    });
    const [actionState, setActionState] = useState({
        userToReset: null,
        statusUser: null,
    });
    const [passwordFormState, setPasswordFormState] = useState(initialPasswordFormState);
    const [userData, setUserData] = useState([]);
    const [formData, setFormData] = useState(getInitialFormData(defaultRoleValue, defaultSalutationValue));
    const [filter, setFilter] = useState(initialFilterState);
    const [showAddUserPassword, setShowAddUserPassword] = useState(false);
    const [showAddUserConfirmPassword, setShowAddUserConfirmPassword] = useState(false);
    const [showResetPassword, setShowResetPassword] = useState(false);
    const [showResetConfirmPassword, setShowResetConfirmPassword] = useState(false);
    const passwordValidationRegex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*[^A-Za-z0-9]).{1,16}$/;
    const validatePasswordValue = (passwordValue) => passwordValidationRegex?.test(passwordValue || "");
    const isValidMobileNumber = /^\d{10}$/.test(formData?.mobileNumber || "");
    const isValidPassword = validatePasswordValue(formData?.password || "");
    const isFormValid = Boolean(formData?.firstName?.trim() && formData?.lastName?.trim() && isValidMobileNumber && formData?.emailId?.trim() && formData?.salutation && formData?.role && formData?.password && formData?.confirmPassword && isValidPassword && formData?.password === formData?.confirmPassword);
    const updateModalState = (key, value) => {
        setModalState((previousState) => ({
            ...previousState,
            [key]: value
        }));
    };
    const updateActionState = (key, value) => {
        setActionState((previousState) => ({
            ...previousState,
            [key]: value
        }));
    };
    
    const getAllUsers = (params) => api.get(`${ADMINUSERMANAGEMENT}/page?page=${params?.page}&size=${params?.size}${params?.searchTermValue ? `&commonSearch=${params?.searchTermValue}` : ""}`);
    const createUser = (payload) => api.post(`${USERMANAGEMENT}/create_user`, payload);
    const changePassword = (payload) => api.post(`${USERMANAGEMENT}/admin/changePassword`, payload);
    const changeUserStatus = (payload) => api.patch(`${ADMINUSERMANAGEMENT}/change-userStatus`, payload);
    const userListQueryKey = ["FETCH_ALL_USERS", filter?.page, filter?.size, filter?.searchTermValue];

    const { data: usersData, isFetching: isFetchingUsers, refetch: refetchUsers } = useQuery(
        userListQueryKey,
        () => getAllUsers(filter),
        {
            onSuccess: (usersResponse) => {
                if (usersResponse?.statusCode === 200 || usersResponse?.status === "OK") {
                    const content = usersResponse?.result?.adminUserResponse?.content || [];
                    const mappedUserData = content?.map((adminUserDetail) => {
                        const firstName = adminUserDetail?.firstName?.trim() || "";
                        const middleName = adminUserDetail?.middleName?.trim() || "";
                        const lastName = adminUserDetail?.lastName?.trim() || "";
                        const statusValue = (adminUserDetail?.userStatus || adminUserDetail?.status || "ACTIVE")?.toUpperCase();
                        const fullName = [firstName, middleName, lastName].filter(Boolean).join(" ") || "Nil";
                        return {
                            id: adminUserDetail?.adminUserId || "",
                            name: fullName,
                            userName: adminUserDetail?.adminUserNo || "",
                            mobileNumber: adminUserDetail?.mobileNumber || "",
                            emailId: adminUserDetail?.email || "",
                            role: adminUserDetail?.role || "Nil",
                            status: statusValue
                        };
                    });
                    setUserData(mappedUserData || []);
                }
                else {
                    setUserData([]);
                }
            },
            enabled: Boolean(filter?.page !== undefined && filter?.size),
            refetchOnWindowFocus: false,
        }
    );
    const totalElements = usersData?.result?.adminUserResponse?.totalElements || 0;
    const shouldShowPagination = totalElements > 10;
    
    const { isFetching: isCreatingUser } = useQuery(
        ["CREATE_USER", ""],
        createUser,
        {
            onSuccess: (createUserResponse) => {
                if (createUserResponse?.data?.statusCode === 200 || createUserResponse?.data?.statusCode === 201 || createUserResponse?.data?.status === "OK") {
                    showSuccessNotification("Success", createUserResponse?.data?.result?.message || createUserResponse?.data?.result?.result || "User created successfully");
                    updateModalState("isAddUserDrawerOpen", false);
                    refetchUsers();
                } else {
                    showErrorNotification("Error", createUserResponse?.response?.data?.error?.message || createUserResponse?.data?.error?.message || createUserResponse?.message || "Failed to create user");
                }
            },
            enabled: false,
            retry: false,
            refetchOnWindowFocus: false,
        }
    );

    const { isFetching: isChangingPassword } = useQuery(
        ["CHANGE_ADMIN_PASSWORD", ""],
        changePassword,
        {
            onSuccess: (changePasswordResponse) => {
                if (changePasswordResponse?.data?.statusCode === 200 || changePasswordResponse?.data?.status === "OK") {
                    showSuccessNotification("Success", changePasswordResponse?.data?.result?.changePassword || "Password changed successfully");
                    setModalState((previousState) => ({
                        ...previousState,
                        isResetModalOpen: false
                    }));
                    setActionState((previousState) => ({
                        ...previousState,
                        userToReset: null,
                    }));
                    setPasswordFormState(initialPasswordFormState);
                    refetchUsers();
                } else {
                    showErrorNotification("Error", changePasswordResponse?.response?.data?.error?.message || changePasswordResponse?.data?.error?.message || changePasswordResponse?.message || "Failed to change password");
                }
            },
            enabled: false,
            retry: false,
            refetchOnWindowFocus: false,
        }
    );

    const { isFetching: isChangingUserStatus } = useQuery(
        ["CHANGE_ADMIN_USER_STATUS", ""],
        changeUserStatus,
        {
            onSuccess: (changeUserStatusResponse) => {
                if (changeUserStatusResponse?.data?.statusCode === 200 || changeUserStatusResponse?.data?.status === "OK") {
                    showSuccessNotification("Success", changeUserStatusResponse?.data?.result?.changeUserStatus || "Admin user updated successfully");
                    setModalState((previousState) => ({
                        ...previousState,
                        isStatusModalOpen: false
                    }));
                    setActionState((previousState) => ({
                        ...previousState,
                        statusUser: null,
                    }));
                    refetchUsers();
                } else {
                    showErrorNotification("Error", changeUserStatusResponse?.response?.data?.error?.message || changeUserStatusResponse?.data?.error?.message || changeUserStatusResponse?.message || "Failed to update user status");
                }
            },
            enabled: false,
            retry: false,
            refetchOnWindowFocus: false,
        }
    );

    const formatMobileNumber = (mobileNumber) => {
        const digitsOnlyNumber = mobileNumber?.replace(/\D/g, "") || "";
        if (!digitsOnlyNumber) {
            return "";
        }
        if (digitsOnlyNumber?.startsWith("91")) {
            return digitsOnlyNumber;
        }
        return `91${digitsOnlyNumber}`;
    };

    const handleFormChange = (key, value) => {
        setFormData(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleMobileNumberChange = (event) => {
        const inputValue = event?.target?.value || "";
        const mobileNumber = inputValue?.replace(/\D/g, "")?.slice(0, 10);
        handleFormChange("mobileNumber", mobileNumber);
    };

    const handlePasswordChange = (event) => {
        const inputValue = event?.target?.value || "";
        handleFormChange("password", inputValue?.slice(0, 16));
    };

    const handleConfirmPasswordChange = (event) => {
        const inputValue = event?.target?.value || "";
        handleFormChange("confirmPassword", inputValue?.slice(0, 16));
    };

    const handleSave = () => {
        if (isFormValid) {
            handleCreateUser(formData);
        }
    };

    const openAddUserDrawer = () => {
        setShowAddUserPassword(false);
        setShowAddUserConfirmPassword(false);
        updateModalState("isAddUserDrawerOpen", true);
    };

    const closeAddUserDrawer = () => {
        setShowAddUserPassword(false);
        setShowAddUserConfirmPassword(false);
        updateModalState("isAddUserDrawerOpen", false);
    };

    const debounceSearch = (searchTerm) => {
        if (debounceTimeOut?.current) {
            clearTimeout(debounceTimeOut?.current);
        }
        if (searchTerm !== "") {
            debounceTimeOut.current = setTimeout(() => {
                setFilter((prev) => ({ ...prev, searchTermValue: searchTerm, page: 0 }));
            }, searchInitiateDelayTime);
        } else {
            setFilter((prev) => ({ ...prev, searchTermValue: "", page: 0 }));
        }
    };

    const handleSearchChange = (event) => {
        const searchTerm = event?.target?.value || "";
        setFilter((prev) => ({ ...prev, searchFilter: searchTerm }));
        debounceSearch(searchTerm);
    };

    const handleSearch = (value) => {
        if (debounceTimeOut?.current) {
            clearTimeout(debounceTimeOut?.current);
        }
        setFilter((prev) => ({ ...prev, searchFilter: value || "", searchTermValue: value || "", page: 0 }));
    };

    const handlePagination = (page, size) => {
        setFilter(prev => ({ ...prev, page: page - 1, size: size }));
    };

    const handleStatusChange = (record) => {
        updateActionState("statusUser", record);
        updateModalState("isStatusModalOpen", true);
    };

    const handleConfirmStatusChange = () => {
        if (actionState?.statusUser?.id) {
            const currentStatus = (actionState?.statusUser?.status || "")?.toUpperCase();
            const nextStatus = currentStatus === "ACTIVE" ? "INACTIVE" : "ACTIVE";
            queryClient.prefetchQuery(["CHANGE_ADMIN_USER_STATUS", ""], () => changeUserStatus({
                userId: actionState?.statusUser?.id || "",
                userStatus: nextStatus
            }));
        }
    };

    const handleCreateUser = (userFormData) => {
        const createUserPayloadData = {
            firstName: userFormData?.firstName?.trim() || "",
            middleName: userFormData?.middleName?.trim() || "",
            lastName: userFormData?.lastName?.trim() || "",
            salutation: userFormData?.salutation?.trim() || "",
            mobileNumber: formatMobileNumber(userFormData?.mobileNumber),
            email: userFormData?.emailId?.trim() || "",
            role: userFormData?.role || "",
            password: userFormData?.password || ""
        };
        queryClient.prefetchQuery(["CREATE_USER", ""], () => createUser(createUserPayloadData));
    };

    const handleResetPassword = (record) => {
        updateActionState("userToReset", record);
        updateModalState("isPasswordFormOpen", true);
        setPasswordFormState(initialPasswordFormState);
        setShowResetPassword(false);
        setShowResetConfirmPassword(false);
    };

    const handlePasswordSave = () => {
        const passwordValue = passwordFormState?.password || "";
        const confirmPasswordValue = passwordFormState?.confirmPassword || "";
        let passwordErrorMessage = "";
        let confirmPasswordErrorMessage = "";
        if (!passwordValue) {
            passwordErrorMessage = "Please enter a password";
        } else if (!validatePasswordValue(passwordValue)) {
            passwordErrorMessage = "Password must contain uppercase, lowercase, symbol and max 16 characters";
        }
        if (!confirmPasswordValue) {
            confirmPasswordErrorMessage = "Please confirm your password";
        } else if (passwordValue !== confirmPasswordValue) {
            confirmPasswordErrorMessage = "Passwords do not match";
        }
        if (passwordErrorMessage || confirmPasswordErrorMessage) {
            setPasswordFormState((previousState) => ({
                ...previousState,
                passwordError: passwordErrorMessage,
                confirmPasswordError: confirmPasswordErrorMessage,
            }));
            return;
        }
        setPasswordFormState((previousState) => ({
            ...previousState,
            passwordError: "",
            confirmPasswordError: "",
        }));
        setModalState((previousState) => ({
            ...previousState,
            isPasswordFormOpen: false,
            isResetModalOpen: true,
        }));
    };

    const handleConfirmReset = () => {
        if (actionState?.userToReset?.id && passwordFormState?.password) {
            queryClient.prefetchQuery(["CHANGE_ADMIN_PASSWORD", ""], () => changePassword({
                userId: actionState?.userToReset?.id || "",
                newPassword: passwordFormState?.password || ""
            }));
        }
    };

    const columns = USER_COLUMNS?.map(col => {
        if (col.key === 'status') {
            return {
                ...col,
                render: (status, record) => (
                    <div
                        style={{ cursor: "pointer", display: 'flex', justifyContent: 'center' }}
                        onClick={() => handleStatusChange(record)}
                    >
                        {col.render(status, record)}
                    </div>
                )
            };
        }
        if (col.key === 'reset') {
            return {
                ...col,
                render: (_, record) => (
                    <div onClick={() => handleResetPassword(record)}>
                        {col.render(_, record)}
                    </div>
                )
            };
        }
        return col;
    });

    useEffect(() => {
        dispatch(loaderReducer(isFetchingUsers || isCreatingUser || isChangingPassword || isChangingUserStatus));
    }, [dispatch, isFetchingUsers, isCreatingUser, isChangingPassword, isChangingUserStatus]);

    useEffect(() => {
        if (modalState?.isAddUserDrawerOpen) {
            setFormData(getInitialFormData(defaultRoleValue, defaultSalutationValue));
        }
    }, [modalState?.isAddUserDrawerOpen, defaultRoleValue, defaultSalutationValue]);

    useEffect(() => {
        return () => {
            if (debounceTimeOut?.current) {
                clearTimeout(debounceTimeOut?.current);
            }
        };
    }, []);

    return (
        <div>
            <div className="flexible">
                <div className="flexible" style={{ alignItems: 'center' }}>
                    <h6>User's</h6> <UiCounterBatch>{totalElements}</UiCounterBatch>
                </div>
                <div className="flexible">
                    <div style={{ width: "300px" }}>
                        <UiSearch
                            placeholder="User Name"
                            value={filter?.searchFilter}
                            onChange={handleSearchChange}
                            handleSearch={handleSearch}
                        />
                    </div>
                    <UiButton
                        onClick={openAddUserDrawer}
                        type="primary"
                        style={{ backgroundColor: "#A67C43", borderColor: "#A67C43" }}
                    >+ Add User
                    </UiButton>
                </div>
            </div>
            <UiTable
                columns={columns}
                dataSource={userData}
                loading={isFetchingUsers}
                pagination={!shouldShowPagination ? false : {
                    current: filter?.page + 1,
                    pageSize: filter?.size,
                    total: totalElements,
                    onChange: handlePagination
                }}
            />
            <AddUserDrawer
                open={modalState?.isAddUserDrawerOpen}
                onClose={closeAddUserDrawer}
                formData={formData}
                handleChange={handleFormChange}
                roleOptions={configTypeOptions}
                salutationOptions={salutationOptions}
                isFormValid={isFormValid}
                handleSave={handleSave}
                handleMobileNumberChange={handleMobileNumberChange}
                handlePasswordChange={handlePasswordChange}
                handleConfirmPasswordChange={handleConfirmPasswordChange}
                showPassword={showAddUserPassword}
                setShowPassword={setShowAddUserPassword}
                showConfirmPassword={showAddUserConfirmPassword}
                setShowConfirmPassword={setShowAddUserConfirmPassword}
            />
            <Modal
                title={<div style={{ textAlign: "center", color: "#444", fontWeight: "bold" }}>Reset the User's Password</div>}
                open={modalState?.isPasswordFormOpen}
                onCancel={() => {
                    updateModalState("isPasswordFormOpen", false);
                    updateActionState("userToReset", null);
                    setPasswordFormState(initialPasswordFormState);
                    setShowResetPassword(false);
                    setShowResetConfirmPassword(false);
                }}
                footer={null}
                centered
                width={400}
                destroyOnClose
            >
                <div style={{ marginTop: "10px" }}>
                    <div style={{ marginBottom: "16px" }}>
                        <UiTextBox
                            label={<span style={{ fontWeight: "bold", color: "#004d40" }}>Password *</span>}
                            type={showResetPassword ? "text" : "password"}
                            value={passwordFormState?.password}
                            onChange={(event) => setPasswordFormState((previousState) => ({
                                ...previousState,
                                password: (event?.target?.value || "")?.slice(0, 16),
                                passwordError: ""
                            }))}
                            maxLength={16}
                            suffix={
                                <span
                                    onClick={() => setShowResetPassword((previousValue) => !previousValue)}
                                    onMouseDown={(event) => event.preventDefault()}
                                    style={{ cursor: "pointer", color: "#999" }}
                                    aria-label={showResetPassword ? "Hide password" : "Show password"}
                                    role="button"
                                >
                                    {showResetPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                                </span>
                            }
                        />
                        {passwordFormState?.passwordError && <span style={{ color: "#ff4d4f", fontSize: "12px", padding: "0 10px" }}>{passwordFormState?.passwordError}</span>}
                    </div>
                    <div style={{ marginBottom: "16px" }}>
                        <UiTextBox
                            label={<span style={{ fontWeight: "bold", color: "#004d40" }}>Re-Password *</span>}
                            type={showResetConfirmPassword ? "text" : "password"}
                            value={passwordFormState?.confirmPassword}
                            onChange={(event) => setPasswordFormState((previousState) => ({
                                ...previousState,
                                confirmPassword: (event?.target?.value || "")?.slice(0, 16),
                                confirmPasswordError: ""
                            }))}
                            maxLength={16}
                            suffix={
                                <span
                                    onClick={() => setShowResetConfirmPassword((previousValue) => !previousValue)}
                                    onMouseDown={(event) => event.preventDefault()}
                                    style={{ cursor: "pointer", color: "#999" }}
                                    aria-label={showResetConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                                    role="button"
                                >
                                    {showResetConfirmPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                                </span>
                            }
                        />
                        {passwordFormState?.confirmPasswordError && <span style={{ color: "#ff4d4f", fontSize: "12px", padding: "0 10px" }}>{passwordFormState?.confirmPasswordError}</span>}
                    </div>
                    <div style={{ display: "flex", justifyContent: "center", gap: "10px", marginTop: "20px" }}>
                        <UiButton
                            onClick={() => {
                                updateModalState("isPasswordFormOpen", false);
                                updateActionState("userToReset", null);
                                setPasswordFormState(initialPasswordFormState);
                                setShowResetPassword(false);
                                setShowResetConfirmPassword(false);
                            }}
                            style={{
                                width: "150px",
                                border: "1px solid #d9d9d9",
                                color: "#333",
                                background: "white",
                                height: "40px",
                            }}
                        >
                            Cancel
                        </UiButton>
                        <UiButton
                            onClick={handlePasswordSave}
                            style={{
                                width: "150px",
                                background: "#A67C43",
                                color: "white",
                                border: "none",
                                height: "40px",
                            }}
                        >
                            Save
                        </UiButton>
                    </div>
                </div>
            </Modal>
            <Modal
                open={modalState?.isStatusModalOpen}
                onCancel={() => {
                    updateModalState("isStatusModalOpen", false);
                    updateActionState("statusUser", null);
                }}
                footer={null}
                centered
                width={450}
                closable={false}
            >
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <div style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        backgroundColor: '#004d40',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 20px auto'
                    }}>
                        <SyncOutlined style={{ fontSize: '30px', color: 'white' }} />
                    </div>
                    <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '30px', color: '#333' }}>
                        Are you sure you want to update this user's status?
                    </h3>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '15px' }}>
                        <UiButton
                            onClick={() => {
                                updateModalState("isStatusModalOpen", false);
                                updateActionState("statusUser", null);
                            }}
                            style={{
                                backgroundColor: 'white',
                                color: '#A67C43',
                                borderColor: '#A67C43',
                                width: '150px',
                                height: "40px",
                                borderRadius: "4px"
                            }}
                        >Cancel</UiButton>
                        <UiButton
                            onClick={handleConfirmStatusChange}
                            loading={isChangingUserStatus}
                            type="primary"
                            style={{
                                backgroundColor: '#C8A355',
                                borderColor: '#C8A355',
                                color: "white",
                                width: '150px',
                                height: "40px",
                                borderRadius: "4px"
                            }}
                        >Proceed</UiButton>
                    </div>
                </div>
            </Modal>
            <Modal
                open={modalState?.isResetModalOpen}
                onCancel={() => {
                    updateModalState("isResetModalOpen", false);
                    updateActionState("userToReset", null);
                    setPasswordFormState(initialPasswordFormState);
                }}
                footer={null}
                centered
                width={400}
                closable={false}
            >
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <div style={{ position: 'relative', display: 'inline-block', marginBottom: '20px' }}>
                        <UserOutlined style={{ fontSize: '48px', color: '#004d40' }} />
                        <CheckCircleFilled style={{ position: 'absolute', bottom: 0, right: -5, fontSize: '20px', color: '#004d40', background: 'white', borderRadius: '50%' }} />
                    </div>

                    <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '30px', color: '#333' }}>
                        Do you want to reset your password now?
                    </h3>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '15px' }}>
                        <UiButton
                            onClick={() => {
                                updateModalState("isResetModalOpen", false);
                                updateActionState("userToReset", null);
                                setPasswordFormState(initialPasswordFormState);
                            }}
                            style={{
                                backgroundColor: 'white',
                                color: '#A67C43',
                                borderColor: '#A67C43',
                                width: '120px'
                            }}
                        >Cancel</UiButton>
                        <UiButton
                            onClick={handleConfirmReset}
                            loading={isChangingPassword}
                            type="primary"
                            style={{
                                backgroundColor: '#A67C43',
                                borderColor: '#A67C43',
                                width: '120px'
                            }}
                        >Reset</UiButton>
                    </div>
                </div>
            </Modal>
        </div>
    );
};


export default UserManagement;

